<template>
  <div class="cc">
    <!-- ================= HEADER ================= -->
    <div class="cc-head">
      <div class="cc-head__title-wrap">
        <span class="cc-head__icon"><i class="fa-solid fa-chess-king"></i></span>
        <div>
          <h2 class="cc-head__title">{{ $t('commandCenter.title') }}</h2>
          <div class="cc-head__subtitle">{{ $t('commandCenter.subtitle') }}</div>
        </div>
      </div>
      <div class="cc-head__actions">
        <span v-if="refreshing" class="cc-head__updating"><b-spinner small class="mr-25"/>{{ $t('operations.loading') }}</span>
      </div>
    </div>

    <!-- Date range: the standard PC preset bar -->
    <calendar-bar v-model="dateRange" @input="onDateRange"/>

    <!-- PC filter row — the same select boxes the other dashboards use:
         picking a desk auto-fills its organizations, orgs narrow the BUs. -->
    <!-- One-click department pills (same widget as the Sales Dashboard), acting
         as a radio on the single department scope this page shares with the
         mobile dashboard (split view + BU narrowing keep working unchanged). -->
    <dept-quick-filter single v-model="filter.department" :org-ids="filter.orgIds" @input="onFilterChanged"/>

    <div class="cc-filters">
      <div class="cc-filters__field">
        <label>{{ $t('affiliateDashboard.desk') }}</label>
        <desk-select-box class="small" :value="filter.desks"
                         :select-props="{ multiple: true, clearable: true }"
                         :placeholder="$t('filters.picker.all')"
                         @input="v => { filter.desks = v }"
                         @resolved="onDeskResolved"/>
      </div>
      <div class="cc-filters__field">
        <label>{{ $t('mobileDash.tabs.organizations') }}</label>
        <organization-select-box class="small" v-model="filter.orgIds"
                                 :select-props="{ multiple: true, clearable: true }"
                                 :placeholder="$t('filters.picker.all')"
                                 @input="onFilterChanged"/>
      </div>
      <div class="cc-filters__field">
        <label>{{ $t('mobileDash.tabs.businessUnits') }}</label>
        <business-unit-select-box class="small" v-model="filter.buIds"
                                  :select-props="{ multiple: true, clearable: true }"
                                  :selected-organization="filter.orgIds"
                                  :selected-department="filter.department"
                                  :placeholder="$t('filters.picker.all')"
                                  @input="onFilterChanged"/>
      </div>
    </div>

    <!-- ================= KPI ROW ================= -->
    <b-overlay :show="summaryLoading && !summary" rounded opacity="0.4" spinner-variant="primary">
      <b-row class="cc-kpis" :class="{ 'cc-dim': summaryLoading && summary }">
        <!-- Hero: TOTAL deposits — the headline number. Inside it: the
             sales/retention split and the pending deposits, each part opening
             its own transaction list (same drill modal as the cards). -->
        <b-col xl="4" md="12" class="mb-2">
          <!-- green = deposits, same tone as the deposit cards elsewhere -->
          <div class="kpi-card kpi-card--success cc-hero">
            <div class="cc-hero__body">
              <!-- Headline = the INCLUSIVE total (approved + still-pending
                   deposits), with the approved-only figure beside it. Each half
                   opens its own transaction list. -->
              <div class="cc-hero__totals">
                <button type="button" class="cc-hero__main" v-b-tooltip.hover
                        :title="$t('salesDashboard.viewTransactions')" @click="openKpiDrill('depositsWithPending')">
                  <span class="kpi-card__title">{{ $t('mobileDash.totalDeposits') }}<i class="fas fa-expand cc-hero__expand"></i></span>
                  <span class="cc-hero__value">{{ convertToEURO(depositsWithPending) }}</span>
                  <span class="cc-hero__meta">
                    <span><i class="fa-regular fa-clock mr-25"></i>{{ $t('mobileDash.withPending') }}</span>
                  </span>
                </button>
                <button type="button" class="cc-hero__approved" v-b-tooltip.hover
                        :title="$t('salesDashboard.viewTransactions')" @click.stop="openKpiDrill('deposits')">
                  <span class="cc-hero__approved-label">
                    <i class="fa-solid fa-circle-check mr-25"></i>{{ $t('financeStatuses.APPROVED') }}
                    <i class="fas fa-expand cc-hero__expand ml-25"></i>
                  </span>
                  <span class="cc-hero__approved-value">{{ convertToEURO(current.totalDeposits || 0) }}</span>
                  <span class="cc-hero__approved-meta">
                    <i class="fa-solid fa-hashtag mr-25"></i>{{ current.depositsCount || 0 }} {{ $t('mobileDash.drill.items') }}
                  </span>
                </button>
              </div>
              <!-- Net = deposits − withdrawals: its list holds BOTH kinds, the modal's sum is the net -->
              <button type="button" class="cc-hero__net" v-b-tooltip.hover
                      :title="$t('salesDashboard.viewTransactions')" @click.stop="openKpiDrill('net')">
                <i class="fa-solid fa-scale-balanced mr-25"></i>{{ $t('mobileDash.net') }}
                <b>{{ convertToEURO(current.netDeposits || 0) }}</b>
                <i class="fas fa-expand cc-hero__expand ml-25"></i>
              </button>

              <!-- Sales vs Retention share — each side is its own drill -->
              <div v-if="!filter.department" class="cc-split">
                <div class="cc-split__bar">
                  <span class="cc-split__seg cc-split__seg--sales" :style="{ width: salesShare + '%' }"></span>
                </div>
                <div class="cc-split__legend">
                  <button type="button" class="cc-split__part" v-b-tooltip.hover
                          :title="$t('salesDashboard.viewTransactions')" @click="openDepartmentDrill('SALES')">
                    <i class="fa-solid fa-star mr-25"></i>{{ $t('mobileDash.sales') }}
                    <b>{{ convertToEURO(current.salesDeposits || 0) }}</b>
                    <span class="cc-split__pct">{{ Math.round(salesShare) }}%</span>
                  </button>
                  <button type="button" class="cc-split__part cc-split__part--right" v-b-tooltip.hover
                          :title="$t('salesDashboard.viewTransactions')" @click="openDepartmentDrill('RETENTION')">
                    <span class="cc-split__pct">{{ Math.round(100 - salesShare) }}%</span>
                    <b>{{ convertToEURO(current.retentionDeposits || 0) }}</b>
                    {{ $t('mobileDash.retention') }}<i class="fa-solid fa-coins ml-25"></i>
                  </button>
                </div>
              </div>

              <!-- Pending deposits (REQUEST/PENDING): part of the deposit picture -->
              <button type="button" class="cc-hero__pending" v-b-tooltip.hover
                      :title="$t('salesDashboard.viewTransactions')" @click="openPendingDrill('DEPOSIT')">
                <span class="cc-hero__pending-row">
                  <span class="cc-hero__pending-label"><i class="fa-regular fa-clock mr-25"></i>{{ $t('monetaryDashboard.pendingDeposits') }}</span>
                  <span class="cc-hero__pending-count">{{ current.pendingDepositsCount || 0 }}</span>
                  <span class="cc-hero__pending-amount">{{ convertToEURO(current.pendingDepositsAmount || 0) }}</span>
                  <i class="fa-solid fa-chevron-right cc-hero__pending-arrow"></i>
                </span>
              </button>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <!-- Pending review — the Finance Dashboard's review queue, second place by request -->
        <b-col xl md="4" sm="6" class="mb-2">
          <div class="kpi-card kpi-card--pending kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')" @click="openPendingReviewDrill">
            <div class="kpi-card__icon"><i class="fa-solid fa-magnifying-glass-dollar"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('finance.pendingReview') }}</div>
              <div class="kpi-card__value">{{ current.pendingReviewCount || 0 }}</div>
              <div class="kpi-card__footer">{{ convertToEURO(current.pendingReviewAmount || 0) }}</div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl md="4" sm="6" class="mb-2">
          <div class="kpi-card kpi-card--danger kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')" @click="openKpiDrill('withdrawals')">
            <div class="kpi-card__icon"><i class="fa-solid fa-arrow-up-from-bracket"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('mobileDash.withdrawals') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(Math.abs(current.totalWithdrawals || 0)) }}</div>
              <div class="kpi-card__footer">{{ current.withdrawalsCount || 0 }} {{ $t('mobileDash.drill.items') }}</div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <!-- FTD — with the lead funnel figures (new leads / duplicated) that used to sit in the hero -->
        <b-col xl md="4" sm="6" class="mb-2">
          <div class="kpi-card kpi-card--warning kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')" @click="openKpiDrill('ftd')">
            <div class="kpi-card__icon"><i class="fa-solid fa-star"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('mobileDash.ftd') }}</div>
              <div class="kpi-card__value">{{ current.ftdCount || 0 }}</div>
              <div class="kpi-card__footer">
                {{ convertToEURO(current.ftdAmount || 0) }}
                <!-- FTD conversion: FTDs as a share of the period's new leads -->
                <span class="cc-pct ml-50">{{ ftdOfLeadsPct }}% {{ $t('mobileDash.ofLeads') }}</span>
              </div>
              <div class="kpi-card__footer cc-kpi-sub">
                <span><i class="fa-solid fa-user-plus mr-25"></i>{{ current.clientsCount || 0 }} {{ $t('mobileDash.newClients').toLowerCase() }}</span>
                <span v-if="current.duplicatedCount"> · <i class="fa-solid fa-copy mr-25"></i>{{ current.duplicatedCount }} {{ $t('affiliateDashboard.duplicated').toLowerCase() }}</span>
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <!-- STD — with its share of the period's FTDs (no period-over-period delta on purpose) -->
        <b-col xl md="4" sm="6" class="mb-2">
          <div class="kpi-card kpi-card--primary kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')" @click="openKpiDrill('std')">
            <div class="kpi-card__icon"><i class="fa-solid fa-medal"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('mobileDash.std') }}</div>
              <div class="kpi-card__value">{{ current.stdCount || 0 }}</div>
              <div class="kpi-card__footer">{{ convertToEURO(current.stdAmount || 0) }}</div>
              <div class="kpi-card__footer"><span class="cc-pct">{{ stdOfFtdPct }}% {{ $t('monetaryDashboard.ofFtds') }}</span></div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <!-- TXF (taxes) — the same CRM-bookkeeping money the Sales and Finance
             dashboards show. Folded into Total Deposits / Net, never FTD/STD. -->
        <b-col xl md="4" sm="6" class="mb-2">
          <div class="kpi-card kpi-card--txf kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.txfHint')" @click="openKpiDrill('txf')">
            <div class="kpi-card__icon"><i class="fa-solid fa-file-invoice-dollar"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('monetaryDashboard.txf') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(current.txfAmount || 0) }}</div>
              <div class="kpi-card__footer">{{ current.txfCount || 0 }} {{ $t('mobileDash.drill.items') }}</div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
      </b-row>
    </b-overlay>

    <!-- ================= MAIN GRID ================= -->
    <b-row>
      <!-- Desk leaderboard -->
      <b-col xl="8" class="mb-2">
        <div class="cc-panel" :class="{ 'cc-dim': rankingsLoading && rankings }">
          <div class="cc-panel__head">
            <span class="cc-panel__icon"><i class="fa-solid fa-table-columns"></i></span>
            <span class="cc-panel__title">{{ $t('mobileDash.byDesk') }}</span>
            <span class="cc-panel__hint">{{ $t('commandCenter.deskHint') }}</span>
          </div>
          <div v-if="!rankingsForbidden">
            <div v-if="!deskRows.length" class="cc-empty">{{ $t('mobileDash.noData') }}</div>
            <button v-for="(row, i) in deskRows" :key="row.key || i" type="button" class="cc-row"
                    @click="openDeskAgents(row)">
              <span class="cc-row__rank" :class="rankClass(i)">{{ i + 1 }}</span>
              <span class="cc-row__main">
                <span class="cc-row__name">{{ row.label }}</span>
                <span class="cc-row__chips">
                  <span class="cc-chip cc-chip--leads">{{ row.leads || 0 }} {{ $t('mobileDash.leadsLabel') }}</span>
                  <span class="cc-chip cc-chip--ftd">{{ row.ftds || 0 }} FTD</span>
                  <span class="cc-chip">{{ row.conversionRate != null ? Number(row.conversionRate).toFixed(1) + '%' : '—' }}</span>
                  <!-- TXF is folded INTO this row's total (its deposit and
                       withdrawal halves go to their own figures, so the sub-line
                       still adds up). The badge says how much of that total is
                       TXF — shown only when there is some. -->
                  <span v-if="row.txf" class="cc-chip cc-chip--txf" v-b-tooltip.hover
                        :title="$t('mobileDash.txfInNet')">TXF {{ convertToEURO(row.txf) }}</span>
                </span>
                <span class="cc-row__bar"><span class="cc-row__bar-fill" :style="{ width: deskShare(row) + '%' }"></span></span>
              </span>
              <span class="cc-row__money">
                <span class="cc-row__net" :class="(row.netTotal || 0) >= 0 ? 'is-pos' : 'is-neg'">{{ convertToEURO(row.netTotal || 0) }}</span>
                <span class="cc-row__sub">
                  <span class="cc-row__in">+{{ convertToEURO(row.netDeposits || 0) }}</span>
                  <span class="cc-row__out">-{{ convertToEURO(Math.abs(row.withdrawals || 0)) }}</span>
                </span>
              </span>
              <i class="fa-solid fa-chevron-right cc-row__chev"></i>
            </button>
          </div>
          <div v-else class="cc-empty">{{ $t('mobileDash.noData') }}</div>
        </div>
      </b-col>

      <!-- Right column: pending + performance -->
      <b-col xl="4" class="mb-2">
        <!-- Needs attention — four tiles, each opening its transaction list:
             pending withdrawals, the finance review queue (Finance Dashboard's
             "Pending review"), FAKE deposits and FAKE withdrawals. Pending
             DEPOSITS moved up into the Total Deposits hero. -->
        <div class="cc-watch mb-2">
          <div class="cc-watch__title">
            <i class="fa-solid fa-bell mr-25"></i>{{ $t('commandCenter.attention') }}
          </div>
          <div class="cc-watch__grid">
            <button type="button" class="cc-watch__tile cc-watch__tile--wd" v-b-tooltip.hover
                    :title="$t('salesDashboard.viewTransactions')" @click="openPendingDrill('WITHDRAWAL')">
              <span class="cc-watch__label"><i class="fa-solid fa-arrow-up mr-25"></i>{{ $t('monetaryDashboard.pendingWithdrawals') }}</span>
              <span class="cc-watch__count">{{ current.pendingWithdrawalsCount || 0 }}</span>
              <span class="cc-watch__amount">{{ convertToEURO(Math.abs(current.pendingWithdrawalsAmount || 0)) }}</span>
            </button>
            <!-- Pending review moved up to its own KPI card (second place) -->
            <button type="button" class="cc-watch__tile cc-watch__tile--fake" v-b-tooltip.hover
                    :title="$t('salesDashboard.viewTransactions')" @click="openFakeDrill('DEPOSIT')">
              <span class="cc-watch__badge">FAKE</span>
              <span class="cc-watch__label"><i class="fa-solid fa-arrow-down mr-25"></i>{{ $t('Fake Deposits') }}</span>
              <span class="cc-watch__count">{{ current.fakeDepositsCount || 0 }}</span>
              <span class="cc-watch__amount">{{ convertToEURO(current.fakeDeposits || 0) }}</span>
            </button>
            <button type="button" class="cc-watch__tile cc-watch__tile--fake" v-b-tooltip.hover
                    :title="$t('salesDashboard.viewTransactions')" @click="openFakeDrill('WITHDRAWAL')">
              <span class="cc-watch__badge">FAKE</span>
              <span class="cc-watch__label"><i class="fa-solid fa-arrow-up mr-25"></i>{{ $t('Fake Withdrawals') }}</span>
              <span class="cc-watch__count">{{ current.fakeWithdrawalsCount || 0 }}</span>
              <span class="cc-watch__amount">{{ convertToEURO(Math.abs(current.fakeWithdrawals || 0)) }}</span>
            </button>
          </div>
        </div>

        <!-- Top performance (brands / units) -->
        <div class="cc-panel" :class="{ 'cc-dim': rankingsLoading && rankings }">
          <div class="cc-panel__head">
            <span class="cc-panel__icon"><i class="fa-solid fa-ranking-star"></i></span>
            <span class="cc-panel__title">{{ $t('mobileDash.performance') }}</span>
            <div class="cc-panel__tabs">
              <button v-for="tab in rankingTabs" :key="tab.key" type="button" class="cc-panel__tab"
                      :class="{ 'is-active': rankingTab === tab.key }" @click="rankingTab = tab.key">
                {{ tab.label }}
              </button>
            </div>
          </div>
          <!-- Desk filter: narrows the brands / units to one desk (panel-local,
               the rest of the page keeps the top filter's scope) -->
          <div v-if="!rankingsForbidden && perfDeskOptions.length > 1" class="cc-panel__tabs cc-panel__tabs--desks mb-1">
            <button type="button" class="cc-panel__tab" :class="{ 'is-active': !activePerfDesk }"
                    @click="perfDesk = null">
              {{ $t('mobileDash.allDesks') }}
            </button>
            <button v-for="desk in perfDeskOptions" :key="desk" type="button" class="cc-panel__tab"
                    :class="{ 'is-active': activePerfDesk === desk }" @click="perfDesk = desk">
              {{ desk }}
            </button>
          </div>
          <div v-if="!rankingsForbidden">
            <div v-if="!rankingRows.length" class="cc-empty">{{ $t('mobileDash.noData') }}</div>
            <button v-for="(row, i) in rankingRows" :key="row.key || i" type="button" class="cc-row cc-row--sm"
                    @click="openRankingRow(row)">
              <span class="cc-row__rank" :class="rankClass(i)">{{ i + 1 }}</span>
              <span class="cc-row__main">
                <span class="cc-row__name">{{ row.label }}</span>
                <span class="cc-row__chips">
                  <span class="cc-chip cc-chip--leads">{{ row.leads || 0 }} {{ $t('mobileDash.leadsLabel') }}</span>
                  <span class="cc-chip cc-chip--ftd">{{ row.ftds || 0 }} FTD</span>
                  <!-- Same as the desk rows: TXF is inside netTotal, the badge says how much -->
                  <span v-if="row.txf" class="cc-chip cc-chip--txf" v-b-tooltip.hover
                        :title="$t('mobileDash.txfInNet')">TXF {{ convertToEURO(row.txf) }}</span>
                </span>
              </span>
              <span class="cc-row__money">
                <span class="cc-row__net" :class="(row.netTotal || 0) >= 0 ? 'is-pos' : 'is-neg'">{{ convertToEURO(row.netTotal || 0) }}</span>
              </span>
              <i class="fa-solid fa-chevron-right cc-row__chev"></i>
            </button>
          </div>
          <div v-else class="cc-empty">{{ $t('mobileDash.noData') }}</div>
        </div>
      </b-col>
    </b-row>

    <!-- Trend -->
    <div class="cc-panel mb-2" :class="{ 'cc-dim': summaryLoading && summary }">
      <div class="cc-panel__head">
        <span class="cc-panel__icon"><i class="fa-solid fa-chart-line"></i></span>
        <span class="cc-panel__title">{{ $t('mobileDash.trend') }}</span>
      </div>
      <md-trend-chart :points="(summary && summary.series) || []" :is-dark-skin="isDarkSkin"/>
    </div>

    <!-- ================= PC MODALS ================= -->
    <!-- Transactions drill: the SAME modal the sales dashboard uses,
         with the Command Center's contrast skin -->
    <insight-transactions-modal ref="txModal" modal-class="cc-modal"/>

    <!-- Per-desk agents: centered PC modal, Sales/Retention tabs -->
    <b-modal v-model="agentModal.visible" size="lg" centered hide-footer
             modal-class="cc-modal" content-class="cc-modal__content" header-class="cc-modal__header"
             :title="agentModal.desk + ' · ' + $t('mobileDash.deskAgents')">
      <b-overlay :show="agentModal.loading" rounded opacity="0.4" spinner-variant="primary" style="min-height: 180px">
        <div v-if="agentTabs.length > 1" class="cc-panel__tabs mb-1">
          <button v-for="t in agentTabs" :key="t.key" type="button" class="cc-panel__tab"
                  :class="{ 'is-active': activeAgentTab === t.key }" @click="agentModal.tab = t.key">
            {{ t.label }}
          </button>
        </div>
        <div v-if="!agentModal.loading && !agentRows.length" class="cc-empty">{{ $t('mobileDash.noData') }}</div>
        <button v-for="(agent, i) in agentRows" :key="agent.userId || i" type="button" class="cc-row cc-row--sm"
                @click="openAgentTx(agent)">
          <span class="cc-row__rank" :class="rankClass(i)">{{ i + 1 }}</span>
          <span class="cc-row__main">
            <span class="cc-row__name">{{ agent.name || $t('commandCenter.txfNoAgent') }}</span>
            <span class="cc-row__chips">
              <template v-if="activeAgentTab === 'txf'">
                <span class="cc-chip cc-chip--txf">{{ agent.count || 0 }} {{ $t('mobileDash.drill.items') }}</span>
                <span v-if="agent.txfDeposits" class="cc-chip">+{{ convertToEURO(agent.txfDeposits) }}</span>
                <span v-if="agent.txfWithdrawals" class="cc-chip">-{{ convertToEURO(Math.abs(agent.txfWithdrawals)) }}</span>
              </template>
              <template v-else-if="activeAgentTab === 'sales'">
                <!-- Same FTD funnel as the sales dashboard: leads received in
                     range (assignment-log receivedCount) → FTD → conversion % -->
                <span class="cc-chip cc-chip--ftd">{{ agent.count || 0 }} FTD</span>
                <span class="cc-chip cc-chip--leads">{{ salesLeadsVal(agent) }} {{ $t('mobileDash.leadsLabel') }}</span>
                <span v-if="salesLeadsVal(agent)" class="cc-chip"
                      v-b-tooltip.hover :title="$t('mobileDash.conversion')">
                  {{ salesConvPct(agent) }}%
                </span>
              </template>
              <template v-else>
                <span class="cc-chip cc-chip--leads">{{ agent.count || 0 }} {{ $t('mobileDash.depositsLabel') }}</span>
                <span v-if="agent.std" class="cc-chip cc-chip--ftd">{{ agent.std }} STD</span>
                <!-- clients handed to this retention agent in range — the sales
                     dashboard's own receivedCount, so both always agree -->
                <span v-if="agent.receivedCount != null" class="cc-chip cc-chip--received"
                      v-b-tooltip.hover :title="$t('salesDashboard.receivedClientsTooltip')">
                  <i class="fa-solid fa-user-plus mr-25"></i>{{ agent.receivedCount }} {{ $t('salesDashboard.received') }}
                </span>
              </template>
            </span>
          </span>
          <span class="cc-row__money">
            <span v-if="activeAgentTab === 'txf'" class="cc-row__net" :class="(agent.txf || 0) >= 0 ? 'is-pos' : 'is-neg'">{{ convertToEURO(agent.txf || 0) }}</span>
            <span v-else class="cc-row__net is-pos">{{ convertToEURO(agent.deposit || 0) }}</span>
          </span>
          <i class="fa-solid fa-chevron-right cc-row__chev"></i>
        </button>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import CalendarBar from '@/components/widget/CalendarBar';
import MdTrendChart from '@/components/mobile/MdTrendChart';
import InsightTransactionsModal from '@/components/modal/InsightTransactionsModal';
import DeskSelectBox from '@/components/widget/select/DeskSelectBox.vue';
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox.vue';
import BusinessUnitSelectBox from '@/components/widget/select/BusinessUnitSelectBox.vue';
import DeptQuickFilter from '@/components/widget/DeptQuickFilter.vue';

/**
 * Command Center — the all-in-one PC dashboard ("mother of all dashboards"),
 * PARALLEL to the mobile Super Dashboard: same store actions and the SAME
 * shared filter (mobileDashboard vuex store), but full desktop chrome:
 * CalendarBar + inline filter selects, PC transactions modal for every drill,
 * centered agents modal, hero net tile, ranked leaderboards.
 */
export default {
  name: 'CommandCenter',
  components: {
    CalendarBar, MdTrendChart, InsightTransactionsModal,
    DeskSelectBox, OrganizationSelectBox, BusinessUnitSelectBox, DeptQuickFilter,
  },
  mixins: [mixinBase, mixinNotifications],
  data() {
    return {
      filter: {
        range: { from: null, until: null },
        department: null,
        desks: [],
        orgIds: [],
        buIds: [],
      },
      dateRange: { from: '', until: '' },
      summary: null,
      summaryLoading: false,
      rankings: null,
      rankingsLoading: false,
      rankingsForbidden: false,
      rankingTab: 'organizations',
      perfDesk: null,
      agentModal: { visible: false, desk: '', loading: false, data: null, tab: 'sales' },
    };
  },
  computed: {
    ...mapGetters('data', ['getOrganizationNameById', 'getBusinessUnitNameById', 'allOrganizations', 'allBusinessUnits']),
    ...mapGetters('mobileDashboard', { storedFilter: 'filter' }),
    isDarkSkin() {
      return this.$store.state.appConfig.layout.skin === 'dark';
    },
    refreshing() {
      return (this.summaryLoading && this.summary != null)
          || (this.rankingsLoading && this.rankings != null);
    },
    current() {
      return (this.summary && this.summary.current) || {};
    },
    // Headline figure: what the period's deposits come to once every pending
    // deposit is approved. The approved-only figure sits beside it in the hero.
    depositsWithPending() {
      return (this.current.totalDeposits || 0) + (this.current.pendingDepositsAmount || 0);
    },
    salesShare() {
      const sales = this.current.salesDeposits || 0;
      const total = sales + (this.current.retentionDeposits || 0);
      if (total <= 0) return 50;
      return Math.min(100, Math.max(0, (sales / total) * 100));
    },
    // STD as a share of the period's FTDs (100 FTD / 40 STD → 40%)
    stdOfFtdPct() {
      const ftd = this.current.ftdCount || 0;
      if (!ftd) return 0;
      return Math.round(((this.current.stdCount || 0) / ftd) * 100);
    },
    // FTD conversion: FTDs as a share of the period's new leads (100 leads / 8 FTD → 8%)
    ftdOfLeadsPct() {
      const leads = this.current.clientsCount || 0;
      if (!leads) return 0;
      return Math.round(((this.current.ftdCount || 0) / leads) * 100);
    },
    deskRows() {
      return (this.rankings || {}).desks || [];
    },
    maxDeskNet() {
      return this.deskRows.reduce((max, r) => Math.max(max, Math.abs(r.netTotal || 0)), 0);
    },
    rankingTabs() {
      return [
        { key: 'organizations', label: this.$t('mobileDash.tabs.organizations') },
        { key: 'businessUnits', label: this.$t('mobileDash.tabs.businessUnits') },
      ];
    },
    // Desks offered by the Top Performance desk filter — the ones the desk
    // leaderboard shows, so the choices follow the page's filter scope.
    perfDeskOptions() {
      return this.deskRows.map(r => r.label || r.key).filter(Boolean);
    },
    // A desk dropped by a filter change falls back to "All desks"
    activePerfDesk() {
      return this.perfDeskOptions.includes(this.perfDesk) ? this.perfDesk : null;
    },
    rankingRows() {
      const rows = (this.rankings || {})[this.rankingTab] || [];
      const desk = this.activePerfDesk;
      if (!desk) return rows;
      const orgIds = new Set(this.deskOrgIds(desk));
      if (this.rankingTab === 'businessUnits') {
        const buOrg = {};
        (this.allBusinessUnits || []).forEach(bu => { buOrg[bu.id] = bu.orgId; });
        return rows.filter(row => orgIds.has(buOrg[row.key]));
      }
      return rows.filter(row => orgIds.has(row.key));
    },
    agentTabs() {
      const tabs = [];
      if ((((this.agentModal.data || {}).salesAgents) || []).length) {
        tabs.push({ key: 'sales', label: this.$t('mobileDash.sales') });
      }
      if ((((this.agentModal.data || {}).retentionAgents) || []).length) {
        tabs.push({ key: 'retention', label: this.$t('mobileDash.retention') });
      }
      // The desk row's TXF badge, split per depositor
      if ((((this.agentModal.data || {}).txfAgents) || []).length) {
        tabs.push({ key: 'txf', label: 'TXF' });
      }
      return tabs;
    },
    activeAgentTab() {
      const tabs = this.agentTabs;
      if (!tabs.length) return null;
      return tabs.some(t => t.key === this.agentModal.tab) ? this.agentModal.tab : tabs[0].key;
    },
    agentRows() {
      const tab = this.activeAgentTab;
      if (!tab) return [];
      const key = { sales: 'salesAgents', retention: 'retentionAgents', txf: 'txfAgents' }[tab];
      return ((this.agentModal.data || {})[key]) || [];
    },
  },
  created() {
    // Reuse the mobile dashboard's persisted scope (department/desks/orgs/BUs);
    // the range comes from THIS page's CalendarBar, which emits on mount and
    // triggers the first fetch.
    if (this.storedFilter) {
      const stored = JSON.parse(JSON.stringify(this.storedFilter));
      this.filter.department = stored.department || null;
      this.filter.desks = stored.desks || [];
      this.filter.orgIds = stored.orgIds || [];
      this.filter.buIds = stored.buIds || [];
    }
  },
  methods: {
    ...mapActions('mobileDashboard', ['getSummary', 'getRankings', 'getDeskAgents']),
    ...mapMutations('mobileDashboard', ['setFilter']),
    rankClass(index) {
      return index === 0 ? 'cc-row__rank--gold'
          : index === 1 ? 'cc-row__rank--silver'
              : index === 2 ? 'cc-row__rank--bronze' : '';
    },
    deskShare(row) {
      if (!this.maxDeskNet) return 0;
      return Math.round(Math.abs(row.netTotal || 0) / this.maxDeskNet * 100);
    },

    // ---------------------------------------------------------- filters
    onDateRange(range) {
      if (!range) return;
      this.filter.range = { from: range.from, until: range.until };
      this.persistFilter();
      this.fetchAll();
    },
    onFilterChanged() {
      this.persistFilter();
      this.fetchAll();
    },
    // Desk picked → auto-fill its organizations in the org select (same
    // cascade the other dashboards' filter card does); the BU select narrows
    // itself from the selected orgs. Clearing the desk clears the fill.
    onDeskResolved({ orgIds }) {
      this.filter.orgIds = orgIds || [];
      this.onFilterChanged();
    },
    persistFilter() {
      // Shared with the mobile page (custom preset so it mirrors the dates).
      this.setFilter(JSON.parse(JSON.stringify({ ...this.filter, preset: 'custom' })));
    },

    // ---------------------------------------------------------- drill-down
    // Identical semantics to the mobile page: the drill list shows the same
    // transactions the KPI counted (desk/department resolved client-side).
    drillScope() {
      const selectedOrgIds = (this.filter.orgIds && this.filter.orgIds.length) ? this.filter.orgIds : null;
      let orgIds = selectedOrgIds;
      if ((this.filter.desks || []).length) {
        const desks = new Set(this.filter.desks);
        const deskOrgIds = (this.allOrganizations || [])
            .filter(org => desks.has(org.desk && org.desk.length ? org.desk : 'No Desk'))
            .map(org => org.id);
        orgIds = selectedOrgIds ? deskOrgIds.filter(id => selectedOrgIds.includes(id)) : deskOrgIds;
        if (!orgIds.length) orgIds = ['0'];
      }
      let buIds = (this.filter.buIds && this.filter.buIds.length) ? this.filter.buIds : null;
      if (this.filter.department) {
        const typed = (this.allBusinessUnits || [])
            .filter(bu => bu.type === this.filter.department)
            .filter(bu => !orgIds || orgIds.includes(bu.orgId))
            .map(bu => bu.id)
            .filter(id => !buIds || buIds.includes(id));
        buIds = typed.length ? typed : ['0'];
      }
      return { orgIds, businessUnit: buIds };
    },
    kpiTxFilters(extra) {
      const filters = {
        type: 'DEPOSIT',
        action: ['CREDIT_LINE', 'TAXES'],
        status: ['APPROVED'],
        isTest: false,
        realType: ['REAL'],
        ...this.drillScope(),
        ...(extra || {}),
      };
      const range = this.filter.range || {};
      if (range.from) {
        filters.registrationTime = {
          start: this.$moment(range.from).startOf('day').toServerDateTimeFormat(),
          end: this.$moment(range.until || range.from).endOf('day').toServerDateTimeFormat(),
        };
      }
      return filters;
    },
    openDrill(title, extra) {
      this.$refs.txModal.show(title, this.kpiTxFilters(extra));
    },
    openKpiDrill(kind) {
      if (kind === 'depositsWithPending') {
        // Headline total: approved deposits AND the ones still waiting, so the
        // modal's sum is the same number the card shows.
        this.openDrill(this.$t('mobileDash.withPending'), { status: ['APPROVED', 'REQUEST', 'PENDING'] });
      } else if (kind === 'ftd') {
        this.openDrill(this.$t('mobileDash.ftd'), { ftdFamily: true, excludeRefundedFtdClients: true, action: ['CREDIT_LINE'] });
      } else if (kind === 'std') {
        this.openDrill(this.$t('mobileDash.std'), { isStd: true, action: ['CREDIT_LINE'] });
      } else if (kind === 'txf') {
        // TXF (taxes) only — the same list the Sales Dashboard's TXF card opens
        this.openDrill(this.$t('monetaryDashboard.txf'), { action: ['TAXES'] });
      } else if (kind === 'withdrawals') {
        this.openDrill(this.$t('mobileDash.withdrawals'), { type: 'WITHDRAWAL', action: ['CREDIT_LINE'] });
      } else if (kind === 'net') {
        // Both deposits and withdrawals (no type filter) — the modal's sum IS the net
        this.openDrill(this.$t('mobileDash.net'), { type: null });
      } else {
        this.openDrill(this.$t('mobileDash.deposits'), {});
      }
    },
    openPendingDrill(type) {
      const label = type === 'DEPOSIT'
          ? this.$t('monetaryDashboard.pendingDeposits')
          : this.$t('monetaryDashboard.pendingWithdrawals');
      this.openDrill(label, { type, status: ['REQUEST', 'PENDING'], action: ['CREDIT_LINE'] });
    },
    // Hero split: the sales money IS the FTD money (sales dashboard rule) and
    // the retention money is the dashboard's retention bucket — the same two
    // lists the Sales Dashboard opens for its split.
    openDepartmentDrill(department) {
      if (department === 'SALES') {
        this.openDrill(this.$t('mobileDash.sales'), { ftdFamily: true, excludeRefundedFtdClients: true, action: ['CREDIT_LINE'] });
      } else {
        this.openDrill(this.$t('mobileDash.retention'), { retentionBucket: true, action: ['CREDIT_LINE'] });
      }
    },
    // Finance review queue — the Finance Dashboard's "Pending review" bucket:
    // approved (or canceled) deposits whose financeStatus is still PENDING.
    openPendingReviewDrill() {
      this.openDrill(this.$t('finance.pendingReview'), { status: ['APPROVED', 'CANCELED'], financeStatus: ['PENDING'] });
    },
    // FAKE money — approved transactions marked realType FAKE (monetary dashboard rule)
    openFakeDrill(type) {
      if (type === 'DEPOSIT') {
        this.openDrill(this.$t('Fake Deposits'), { realType: ['FAKE'] });
      } else {
        this.openDrill(this.$t('Fake Withdrawals'), { type: 'WITHDRAWAL', realType: ['FAKE'], action: ['CREDIT_LINE'] });
      }
    },
    openDeskAgents(row) {
      const desk = row.label || row.key;
      if (!desk) return;
      this.agentModal = { visible: true, desk, loading: true, data: null, tab: 'sales' };
      this.getDeskAgents({ ...this.filter, desks: [desk] })
          .then(data => {
            this.agentModal.data = data;
          })
          .catch(err => {
            this.showErrorNotification(this.$t('mobileDash.noData'), this.getErrorDescription(err));
          })
          .finally(() => {
            this.agentModal.loading = false;
          });
    },
    // Sales-tab funnel figures — the sales dashboard's own leadsVal/convPct:
    // leads = receivedCount (assignment log) with legacy pending-leads fallback.
    salesLeadsVal(agent) {
      return agent.receivedCount != null ? agent.receivedCount : (agent.leads || 0);
    },
    salesConvPct(agent) {
      const leads = this.salesLeadsVal(agent);
      if (!leads) return '0';
      return Math.round((agent.count || 0) / leads * 100);
    },
    openAgentTx(agent) {
      if (!agent.userId) return;
      const kind = this.activeAgentTab;
      if (kind === 'txf') {
        // that agent's TXF rows — deposits AND withdrawals, so the sum is his TXF figure
        this.openDrill(`${this.agentModal.desk} · ${agent.name} · TXF`,
            { type: null, action: ['TAXES'], userIds: [agent.userId], orgIds: this.deskOrgIds(this.agentModal.desk) });
        return;
      }
      const extra = kind === 'sales'
          ? { ftdFamily: true, excludeRefundedFtdClients: true, action: ['CREDIT_LINE'], userIds: [agent.userId] }
          : { isFtd: false, action: ['CREDIT_LINE'], userIds: [agent.userId] };
      this.openDrill(`${this.agentModal.desk} · ${agent.name}`, { ...extra, orgIds: this.deskOrgIds(this.agentModal.desk) });
    },
    deskOrgIds(desk) {
      const ids = (this.allOrganizations || [])
          .filter(org => (org.desk && org.desk.length ? org.desk : 'No Desk') === desk)
          .map(org => org.id);
      return ids.length ? ids : ['0'];
    },
    openRankingRow(row) {
      if (this.rankingTab === 'businessUnits') {
        this.openDrill(row.label, { businessUnit: [row.key] });
      } else {
        this.openDrill(row.label, { orgIds: [row.key] });
      }
    },

    // ---------------------------------------------------------- data
    fetchAll() {
      this.fetchSummary();
      this.fetchRankings();
    },
    fetchSummary() {
      this.summaryLoading = true;
      this.getSummary(this.filter)
          .then(summary => {
            this.summary = summary;
          })
          .catch(err => {
            console.error('command center summary error', err, this.getErrorDescription(err));
          })
          .finally(() => {
            this.summaryLoading = false;
          });
    },
    fetchRankings() {
      if (this.rankingsForbidden) return;
      this.rankingsLoading = true;
      this.getRankings(this.filter)
          .then(rankings => {
            this.rankings = rankings;
          })
          .catch(err => {
            if (err.response && err.response.status === 403) {
              this.rankingsForbidden = true;
            }
          })
          .finally(() => {
            this.rankingsLoading = false;
          });
    },
  },
};
</script>

<style lang="scss" scoped>
/* ================= HEADER ================= */
.cc-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-bottom: 0.9rem;
}
.cc-head__title-wrap { display: flex; align-items: center; gap: 0.9rem; }
.cc-head__icon {
  width: 52px;
  height: 52px;
  border-radius: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #fff;
  background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #6d28d9 130%);
  box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.45),
      inset 0 -3px 6px rgba(0, 0, 0, 0.25),
      0 10px 22px -8px rgba(0, 123, 255, 0.65);
}
.cc-head__title {
  margin: 0;
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #2f2b50;
}
.cc-head__subtitle { font-size: 0.85rem; color: #6e6b7b; font-weight: 500; }
.cc-head__actions { margin-left: auto; display: flex; align-items: center; gap: 0.5rem; }
.cc-head__updating { font-size: 0.8rem; color: #b9b9c3; display: inline-flex; align-items: center; }

/* ================= FILTER ROW ================= */
.cc-filters {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.7rem;
  margin-bottom: 1.1rem;

  &__field label {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6e6b7b;
    margin-bottom: 0.25rem;
  }
}
@media (max-width: 992px) {
  .cc-filters { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 576px) {
  .cc-filters { grid-template-columns: 1fr; }
}

.cc-dim { opacity: 0.55; transition: opacity 0.2s ease; }

/* ================= HERO ================= */
/* All KPI cards in the row share ONE height (the columns stretch, the cards fill them) */
.cc-kpis .kpi-card { height: 100%; min-height: 0; }
.cc-hero {
  align-items: flex-start;
  padding: 1rem 1.25rem 0.9rem;
  /* deeper green than the small deposit cards: white text + gold/amber accents stay readable */
  &.kpi-card--success { background: linear-gradient(135deg, #0ea371 0%, #047857 55%, #065f46 100%); }
  .kpi-card__title { color: rgba(255, 255, 255, 0.92); text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25); }
  .cc-hero__value, .cc-split__legend { text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3); }
  .cc-hero__meta { opacity: 1; color: rgba(255, 255, 255, 0.9); }
}
.cc-hero__body { position: relative; z-index: 2; width: 100%; min-width: 0; }
.cc-hero__value {
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cc-hero__meta {
  display: flex;
  gap: 1.2rem;
  margin-top: 0.2rem;
  font-size: 0.82rem;
  opacity: 0.92;
  flex-wrap: wrap;
}
.cc-split { margin-top: 0.65rem; }
.cc-split__bar {
  height: 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.38);
  overflow: hidden;
  /* sunken track */
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.35), inset 0 -1px 0 rgba(255, 255, 255, 0.12);
}
.cc-split__seg--sales {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #ffd27f, #ffb020);
  /* glossy pill sitting IN the track */
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55), inset 0 -2px 3px rgba(0, 0, 0, 0.18);
  transition: width 0.4s ease;
}
.cc-split__legend {
  display: flex;
  justify-content: space-between;
  margin-top: 0.45rem;
  font-size: 0.8rem;
  font-weight: 700;
  opacity: 0.95;
  flex-wrap: wrap;
  gap: 0.3rem;
}

/* ================= PANELS ================= */
.cc-panel {
  background: linear-gradient(180deg, #ffffff 0%, #fafbfe 100%);
  border: 1px solid rgba(34, 41, 47, 0.08);
  border-radius: 16px;
  padding: 1.15rem 1.25rem;
  /* raised slab: top highlight + layered ambient/key shadows */
  box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.9),
      0 12px 30px -14px rgba(20, 24, 50, 0.22),
      0 2px 6px rgba(20, 24, 50, 0.06);
  height: 100%;
}
.cc-panel__head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
}
.cc-panel__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: #007BFF;
  background: rgba(0, 123, 255, 0.1);
  flex-shrink: 0;
}
.cc-panel__title {
  font-size: 0.95rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5e5873;
}
.cc-panel__hint { font-size: 0.76rem; color: #b9b9c3; }
.cc-panel__tabs { margin-left: auto; display: flex; gap: 6px; }
.cc-panel__tabs--desks { margin-left: 0; flex-wrap: wrap; }
.cc-panel__tab {
  border: none;
  border-radius: 999px;
  padding: 5px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  background: rgba(138, 145, 156, 0.12);
  color: #8a919c;
  cursor: pointer;
  transition: all 0.15s ease;
  &.is-active { background: rgba(0, 123, 255, 0.12); color: #007BFF; }
}
.cc-empty { text-align: center; color: #b9b9c3; font-size: 0.9rem; padding: 1.6rem 0; }

/* ================= LEADERBOARD ROWS ================= */
.cc-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.85rem 0.6rem;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  & + .cc-row { border-top: 1px solid rgba(34, 41, 47, 0.05); }

  &:hover {
    background: rgba(0, 123, 255, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 8px 18px -10px rgba(20, 24, 50, 0.28);
    .cc-row__chev { opacity: 1; transform: translateX(2px); }
  }

  &__rank {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    font-weight: 800;
    background: rgba(138, 145, 156, 0.12);
    color: #8a919c;
    flex-shrink: 0;
    /* embossed medal: top gloss + bottom shade + drop shadow */
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.5),
        inset 0 -2px 3px rgba(0, 0, 0, 0.12),
        0 3px 6px -2px rgba(20, 24, 50, 0.2);

    &--gold { background: linear-gradient(135deg, #fde68a, #f59e0b); color: #7c4a03; }
    &--silver { background: linear-gradient(135deg, #e5e7eb, #9ca3af); color: #374151; }
    &--bronze { background: linear-gradient(135deg, #fcd9b6, #d97706); color: #6b3305; }
  }

  &__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.3rem; }
  &__name {
    font-size: 1.05rem;
    font-weight: 700;
    color: #5e5873;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__chips { display: flex; gap: 0.35rem; flex-wrap: wrap; }
  &__bar {
    height: 5px;
    border-radius: 999px;
    background: rgba(138, 145, 156, 0.14);
    overflow: hidden;
    margin-top: 0.2rem;
    box-shadow: inset 0 1px 2px rgba(20, 24, 50, 0.18); /* sunken track */
  }
  &__bar-fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #4da3ff, #007BFF);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45); /* gloss */
    transition: width 0.4s ease;
  }

  &__money { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
  &__net {
    font-size: 1.15rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    &.is-pos { color: #1f9d61; }
    &.is-neg { color: #d64545; }
  }
  &__sub { display: flex; gap: 0.5rem; font-size: 0.78rem; font-weight: 700; }
  &__in { color: #1f9d61; }
  &__out { color: #d64545; }

  &__chev {
    color: #b9b9c3;
    font-size: 0.78rem;
    opacity: 0;
    transition: all 0.15s ease;
    flex-shrink: 0;
  }

  &--sm {
    padding: 0.6rem 0.5rem;
    .cc-row__name { font-size: 0.92rem; }
    .cc-row__net { font-size: 0.98rem; }
  }
}

.cc-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  background: rgba(138, 145, 156, 0.12);
  color: #8a919c;
  font-size: 0.74rem;
  font-weight: 700;
  white-space: nowrap;

  &--leads { background: rgba(0, 123, 255, 0.1); color: #007BFF; }
  &--ftd { background: rgba(255, 159, 67, 0.14); color: #ff9f43; }
  /* received clients — the sales dashboard's solid blue "received" pill */
  &--received { background: linear-gradient(135deg, #2f7fd6, #4da3ff); color: #fff; }
  /* TXF: teal, the same family as the TXF KPI card, and never confused with
     the net figure on the right because it carries its own "TXF" prefix. */
  &--txf { background: rgba(20, 184, 166, 0.14); color: #0d9488; }
}

/* ================= KPI CARDS (desktop card language) ================= */
.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1.15rem 1.2rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  /* 3D lighting: top-edge highlight (inset) + ambient + key-light shadows */
  box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.28),
      inset 0 -14px 24px -18px rgba(0, 0, 0, 0.35),
      0 16px 32px -16px rgba(20, 24, 50, 0.4),
      0 3px 6px rgba(20, 24, 50, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 150px;

  /* calm hover: a small lift, no tilt/scale */
  &.kpi-card--click:hover {
    transform: translateY(-2px);
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.32),
        inset 0 -14px 24px -18px rgba(0, 0, 0, 0.32),
        0 20px 36px -18px rgba(20, 24, 50, 0.45),
        0 4px 8px rgba(20, 24, 50, 0.1);
  }
  &.kpi-card--click:active {
    transform: translateY(0);
  }

  &__icon {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
    font-size: 1.15rem;
    /* raised chip: light from above, soft drop below */
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.45),
        inset 0 -2px 4px rgba(0, 0, 0, 0.18),
        0 4px 8px -3px rgba(0, 0, 0, 0.28);
  }
  &__body { position: relative; z-index: 2; flex: 1; min-width: 0; }
  &__title {
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.92;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__value {
    font-size: 1.9rem;
    font-weight: 800;
    line-height: 1.15;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* secondary figures (amount / count / leads): readable, not a footnote */
  &__footer {
    margin-top: 0.45rem;
    font-size: 0.98rem;
    font-weight: 600;
    opacity: 0.96;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__footer + &__footer { margin-top: 0.3rem; }
  &__glow {
    position: absolute;
    top: -40%;
    right: -20%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }
  &__expand {
    position: absolute;
    top: 10px;
    right: 12px;
    z-index: 3;
    font-size: 0.72rem;
    opacity: 0;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
  &--click {
    cursor: pointer;
    &:focus { outline: none; }
    &:hover .kpi-card__expand { opacity: 0.95; transform: scale(1.15); }
  }

  &--primary { background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%); }
  &--success { background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%); }
  &--warning { background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%); }
  &--danger  { background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%); }
  &--txf     { background: linear-gradient(135deg, #2dd4bf 0%, #14b8a6 55%, #0f766e 100%); }
  &--pending { background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 55%, #5b21b6 100%); }

  &--split {
    padding: 0;
    min-height: 120px;

    .kpi-half {
      position: absolute;
      inset: 0;
      border: none;
      background: transparent;
      color: #fff;
      cursor: pointer;
      z-index: 2;
      padding: 0;
      transition: background 0.18s ease;
      &:focus { outline: none; }
      &--dep {
        clip-path: polygon(0 0, 100% 0, 0 100%);
        background: rgba(255, 255, 255, 0.10);
        &:hover { background: rgba(255, 255, 255, 0.4); }
      }
      &--wd {
        clip-path: polygon(100% 0, 100% 100%, 0 100%);
        background: rgba(0, 0, 0, 0.16);
        &:hover { background: rgba(0, 0, 0, 0.45); }
      }
      &:hover .kpi-half__inner { transform: scale(1.08); }
    }
    .kpi-half__inner {
      position: absolute;
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      transition: transform 0.18s ease;
    }
    .kpi-half--dep .kpi-half__inner { top: 13px; left: 16px; align-items: flex-start; text-align: left; transform-origin: top left; }
    .kpi-half--wd .kpi-half__inner { bottom: 13px; right: 16px; align-items: flex-end; text-align: right; transform-origin: bottom right; }
    .kpi-half__title {
      font-size: 0.68rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.92;
      display: inline-flex;
      align-items: center;
    }
    .kpi-half__value { font-size: 1.45rem; font-weight: 800; line-height: 1.1; }
    .kpi-half__amount {
      font-size: 0.82rem;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      opacity: 0.92;
      white-space: nowrap;
    }
  }
}
.cc-pending { min-height: 128px; }

/* ===== Hero internals: clickable headline, split parts, pending deposits row ===== */
/* Headline row: the inclusive total on the left, the approved-only figure in
   its own glass box on the right. Wraps on narrow cards so neither is clipped. */
.cc-hero__totals {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.cc-hero__approved {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.1rem;
  margin-top: 0.15rem;
  padding: 0.35rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.28);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(0, 0, 0, 0.4); }
  &:hover .cc-hero__expand { opacity: 1; }
  &:focus { outline: none; }
}
.cc-hero__approved-label {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #b7f7d4;
}
.cc-hero__approved-value {
  font-size: 1.25rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.cc-hero__approved-meta { font-size: 0.72rem; font-weight: 600; opacity: 0.9; white-space: nowrap; }
.cc-hero__main {
  display: block;
  /* shares the headline row with the approved box — grow, never force a wrap */
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  border: 0;
  background: transparent;
  color: inherit;
  padding: 0;
  cursor: pointer;
  &:focus { outline: none; }
  &:hover .cc-hero__expand { opacity: 1; transform: scale(1.1); }
  .kpi-card__title { display: flex; align-items: center; gap: 0.5rem; }
  .cc-hero__value, .cc-hero__meta { display: block; }
  .cc-hero__meta { display: flex; }
}
.cc-hero__expand { font-size: 0.8rem; opacity: 0.6; transition: all 0.15s ease; }
.cc-hero__net {
  margin-top: 0.35rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.28);
  color: #fff;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.82rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  &:hover { background: rgba(0, 0, 0, 0.4); }
  &:hover .cc-hero__expand { opacity: 1; }
  &:focus { outline: none; }
}
.cc-split__part {
  border: 0;
  background: transparent;
  color: inherit;
  padding: 0.15rem 0.35rem;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  &:hover { background: rgba(255, 255, 255, 0.14); }
  &:focus { outline: none; }
}
.cc-split__pct {
  font-size: 0.7rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
}
.cc-hero__pending {
  margin-top: 0.6rem;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 210, 127, 0.55);
  border-radius: 10px;
  /* dark glass with an amber edge — the pending numbers pop on the green card */
  background: rgba(0, 0, 0, 0.28);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(0, 0, 0, 0.4); }
  &:focus { outline: none; }
}
.cc-hero__pending { flex-direction: column; align-items: stretch; gap: 0.3rem; }
.cc-hero__pending-row { display: flex; align-items: center; gap: 0.6rem; width: 100%; }
.cc-hero__pending-label { font-size: 0.8rem; font-weight: 700; flex: 1; text-align: left; color: #ffd27f; }
.cc-hero__pending-count { font-size: 1.25rem; font-weight: 800; font-variant-numeric: tabular-nums; }
.cc-hero__pending-amount { font-size: 0.85rem; font-weight: 700; opacity: 0.95; font-variant-numeric: tabular-nums; }
.cc-hero__pending-arrow { font-size: 0.75rem; opacity: 0.75; }

/* Card extras: FTD funnel line, STD share badge */
.cc-kpi-sub { font-size: 0.9rem; font-weight: 600; opacity: 0.95; }
.cc-pct {
  display: inline-block;
  padding: 0.15rem 0.65rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-weight: 800;
  font-size: 0.95rem;
}

/* ===== Needs attention — 2x2 tiles ===== */
.cc-watch {
  padding: 0.85rem 0.85rem 0.95rem;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfe 100%);
  border: 1px solid rgba(34, 41, 47, 0.08);
  box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.9),
      0 12px 30px -14px rgba(20, 24, 50, 0.22),
      0 2px 6px rgba(20, 24, 50, 0.06);
}
.cc-watch__title {
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5e5873;
  margin-bottom: 0.65rem;
}
.cc-watch__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 0.6rem;
}
.cc-watch__tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  padding: 0.7rem 0.8rem;
  border-radius: 12px;
  border: 1px solid transparent;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 8px 18px -8px rgba(20, 24, 50, 0.35); }
  &:focus { outline: none; }
  &--wd {
    background: rgba(234, 84, 85, 0.1);
    border-color: rgba(234, 84, 85, 0.35);
    .cc-watch__label, .cc-watch__amount { color: #d64545; }
  }
  &--review {
    background: rgba(255, 159, 67, 0.12);
    border-color: rgba(255, 159, 67, 0.4);
    .cc-watch__label, .cc-watch__amount { color: #c47b28; }
  }
  &--fake {
    background: repeating-linear-gradient(135deg, rgba(240, 134, 43, 0.1) 0 8px, rgba(240, 134, 43, 0.05) 8px 16px);
    border: 1px dashed rgba(240, 134, 43, 0.7);
    .cc-watch__label, .cc-watch__amount { color: #c9641a; }
  }
}
.cc-watch__badge {
  position: absolute;
  top: 6px;
  right: 8px;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  background: #ff9f43;
  color: #fff;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.14em;
}
.cc-watch__label {
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.cc-watch__count {
  font-size: 1.55rem;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: #24292f;
}
.cc-watch__amount { font-size: 0.82rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.dark-layout {
  .cc-watch { background: linear-gradient(180deg, #283046 0%, #242b3d 100%); border-color: rgba(255, 255, 255, 0.08); }
  .cc-watch__title { color: #b4b7bd; }
  .cc-watch__count { color: #d0d2d6; }
}

/* ===== Pending approvals — the mobile dashboard's card, PC-sized ===== */
.cc-pendcard {
  padding: 0.85rem 0.6rem 0.95rem;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 159, 67, 0.12) 0%, rgba(255, 190, 61, 0.07) 100%);
  border: 1px solid rgba(255, 159, 67, 0.3);
  box-shadow: 0 2px 4px rgba(255, 159, 67, 0.08), 0 10px 22px rgba(255, 159, 67, 0.12);
}
.cc-pendcard__title {
  text-align: center;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #c47b28;
  margin-bottom: 0.55rem;
}
.cc-pendcard__split {
  display: flex;
  align-items: stretch;
}
.cc-pendcard__half {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  border: none;
  background: transparent;
  padding: 0.25rem 0.3rem;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(255, 159, 67, 0.14); }
  &:focus { outline: none; }
}
.cc-pendcard__divider {
  width: 1px;
  background: rgba(255, 159, 67, 0.35);
  margin: 2px 0;
}
.cc-pendcard__label {
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.cc-pendcard__label--dep { color: #1f9d61; }
.cc-pendcard__label--wd { color: #d64545; }
.cc-pendcard__count {
  font-size: 1.55rem;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: #24292f;
}
.cc-pendcard__amount {
  font-size: 0.82rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #c47b28;
  white-space: nowrap;
}
.dark-layout .cc-pendcard {
  background: linear-gradient(135deg, rgba(255, 159, 67, 0.16) 0%, rgba(255, 190, 61, 0.08) 100%);
  border-color: rgba(255, 159, 67, 0.4);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.28), 0 10px 22px rgba(0, 0, 0, 0.24);
}
.dark-layout .cc-pendcard__title,
.dark-layout .cc-pendcard__amount { color: #ffb976; }
.dark-layout .cc-pendcard__count { color: #d0d2d6; }

/* ================= DARK MODE ================= */
.dark-layout {
  .cc-head__title { color: #eaecf2; }
  .cc-head__subtitle { color: #b4b7bd; }
  .cc-filters__field label { color: #b4b7bd; }
  .cc-panel {
    background: linear-gradient(180deg, #2b3349 0%, #283046 100%);
    border-color: #3b4253;
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.05),
        0 14px 32px -14px rgba(0, 0, 0, 0.55),
        0 2px 6px rgba(0, 0, 0, 0.3);
  }
  .cc-panel__title { color: #d0d2d6; }
  .cc-row {
    & + .cc-row { border-top-color: rgba(255, 255, 255, 0.06); }
    &:hover {
      background: rgba(77, 163, 255, 0.08);
      box-shadow: 0 8px 18px -10px rgba(0, 0, 0, 0.55);
    }
    &__name { color: #d0d2d6; }
    &__bar { box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.4); }
  }
}

@media (max-width: 768px) {
  .cc-head__actions { margin-left: 0; width: 100%; }
  .cc-hero__value { font-size: 1.9rem; }
  .kpi-card { min-height: 120px; }
}
</style>

<!-- Global (unscoped): modals render in the body outlet — the cc-modal skin
     applies ONLY to modals opened from the Command Center. -->
<style lang="scss">
/* Darker, blurred backdrop so the modal pops off the busy dashboard */
.cc-modal + .modal-backdrop {
  background: #0a0e1a;
  opacity: 0.7;
}

.cc-modal .modal-content {
  border: none;
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
      0 0 0 1px rgba(77, 163, 255, 0.25),
      0 30px 70px -20px rgba(5, 8, 20, 0.75),
      0 10px 24px rgba(5, 8, 20, 0.35);
}

/* Navy hero header — clear contrast against the light page AND the dark one */
.cc-modal .modal-header {
  background: linear-gradient(120deg, #1f2a44, #3b5b92);
  border-bottom: none;
  color: #fff;
  padding: 1rem 1.4rem;

  .modal-title { color: #fff; font-weight: 700; }
  /* The theme turns .close into a white floating card translated out of the
     corner — on the navy band that made the white × invisible (light mode)
     and the button sit offset (dark mode). Rebuild it as a normal in-header
     button: translucent chip, white ×, no transform. */
  .close {
    color: #fff;
    opacity: 1;
    text-shadow: none;
    background: rgba(255, 255, 255, 0.15) !important;
    box-shadow: none !important;
    border-radius: 10px;
    margin: 0;
    padding: 0.3rem 0.7rem !important;
    transform: none !important;
    transition: background 0.15s ease;
    &:hover, &:focus, &:active {
      color: #fff;
      opacity: 1;
      background: rgba(255, 255, 255, 0.3) !important;
      transform: none !important;
      outline: none;
    }
  }
  /* The tx modal's total chips read on the navy band */
  .itm-chip {
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    .itm-chip__label { color: rgba(255, 255, 255, 0.75); }
    .itm-chip__value { color: #fff; }
  }
}

.dark-layout .cc-modal .modal-content {
  background: #232c3e;
  box-shadow:
      0 0 0 1px rgba(77, 163, 255, 0.35),
      0 30px 70px -20px rgba(0, 0, 0, 0.9),
      0 10px 24px rgba(0, 0, 0, 0.5);
}

/* Dark theme's own .close rules have higher specificity — repeat the fix. */
.dark-layout .cc-modal .modal-header .close {
  color: #fff;
  opacity: 1;
  background: rgba(255, 255, 255, 0.15) !important;
  box-shadow: none !important;
  margin: 0;
  padding: 0.3rem 0.7rem !important;
  transform: none !important;
}
.dark-layout .cc-modal .modal-header .close:hover,
.dark-layout .cc-modal .modal-header .close:focus,
.dark-layout .cc-modal .modal-header .close:active {
  color: #fff;
  opacity: 1;
  background: rgba(255, 255, 255, 0.3) !important;
  transform: none !important;
}
</style>
